import React, { memo } from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { ACTION_BUTTON_SX } from '../summary/summaryStyles';

interface FileSlotProps {
  label: string;
  /** Пояснение справа от названия: зачем нужен файл */
  note?: string;
  fileName: string;
  /** Число строк с данными в загруженном файле */
  rowCount: number | null;
  loading: boolean;
  error: string;
  onSelect: (file: File) => void;
  onClear: () => void;
}

const LABEL_SX = {
  fontSize: '0.74rem',
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.4px'
};

/** Одно место загрузки Excel-файла: выбор, имя, число строк, очистка и ошибка чтения */
const FileSlot: React.FC<FileSlotProps> = ({ label, note, fileName, rowCount, loading, error, onSelect, onClear }) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    // Без сброса повторный выбор того же файла (например, после правки в Excel) не вызовет onChange
    event.target.value = '';
    if (file) onSelect(file);
  };

  return (
    <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
        <Typography component="span" sx={LABEL_SX}>{label}</Typography>
        {note && <Typography component="span" sx={{ fontSize: '0.74rem', color: '#94a3b8' }}>{note}</Typography>}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 40 }}>
        <Button
          component="label"
          variant="outlined"
          size="small"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadFileOutlinedIcon />}
          sx={ACTION_BUTTON_SX}
        >
          {loading ? 'Чтение' : fileName ? 'Заменить' : 'Выбрать файл'}
          <input type="file" hidden accept=".xlsx,.xls" onChange={handleChange} />
        </Button>

        <Typography
          noWrap
          title={fileName}
          sx={{ flex: 1, minWidth: 0, fontSize: '0.86rem', color: fileName ? '#1e293b' : '#94a3b8' }}
        >
          {fileName || 'Файл не выбран'}
        </Typography>

        {rowCount !== null && (
          <Typography sx={{ fontSize: '0.78rem', color: '#64748b', whiteSpace: 'nowrap' }}>
            Строк: {rowCount.toLocaleString('ru-RU')}
          </Typography>
        )}

        {fileName && !loading && (
          <Tooltip title="Убрать файл" arrow>
            <IconButton size="small" onClick={onClear} aria-label="Убрать файл">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {error && <Typography sx={{ fontSize: '0.8rem', color: '#b91c1c', mt: 0.25 }}>{error}</Typography>}
    </Box>
  );
};

export default memo(FileSlot);
